import React, {useMemo, useState} from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import {DefaultButton} from "../../../../components/common/DefaultButton";
import {DtxSpacer} from "../../../../components/common/DtxSpacer";
import {
  IClientNotificationSettings,
  IHelpdeskNotificationSettings,
  ISlackNotificationFeatures
} from "../../../../types/ClientNotificationSettings";

// Deliberately loose: the backoffice does the real validation
const emailPattern = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;
export const isValidEmail = (email: string) => emailPattern.test(email);

const timezones: string[] = Intl.supportedValuesOf('timeZone');

// What the postoffice uses for a client without a timezone
export const defaultTimezone = 'Australia/Sydney';

const helpdeskFlags: { key: keyof Omit<IHelpdeskNotificationSettings, 'email'>, label: string }[] = [
  {key: 'inactiveUsers', label: 'Inactive users'},
  {key: 'zombieUsers', label: 'Zombie users'},
  {key: 'newLicensedUser', label: 'New licensed users'},
];

const slackFlags: { key: keyof ISlackNotificationFeatures, label: string }[] = [
  {key: 'connectionCreated', label: 'Connection created'},
  {key: 'connectionUpdated', label: 'Connection updated'},
  {key: 'connectionLoginFailed', label: 'Connection login failed'},
  {key: 'hpuMfaDisabled', label: 'MFA disabled for a highly privileged user'},
];

const newHelpdesk: IHelpdeskNotificationSettings = {
  email: '',
  inactiveUsers: true,
  zombieUsers: true,
  newLicensedUser: true,
};

// Returns the first problem with the settings, or '' if there is none
export const validateNotificationSettings = (settings: IClientNotificationSettings) => {
  const badSummaryEmail = settings.summaryEmails.find(e => !isValidEmail(e));
  if (badSummaryEmail !== undefined) {
    return `"${badSummaryEmail}" is not a valid email address`;
  }
  const badHelpdesk = settings.helpdesk.find(h => !isValidEmail(h.email.trim()));
  if (badHelpdesk !== undefined) {
    return badHelpdesk.email.trim() ?
      `"${badHelpdesk.email.trim()}" is not a valid email address` :
      'Every helpdesk integration needs an email address';
  }
  return '';
}

const SectionHeading = ({children}: { children: React.ReactNode }) => (
  <Typography variant="h6" sx={{fontWeight: 'bold'}}>{children}</Typography>
);

const SectionDescription = ({children}: { children: React.ReactNode }) => {
  const theme = useTheme();
  return <Typography sx={{color: theme.palette.text.secondary, marginBottom: theme.spacing(1)}}>
    {children}
  </Typography>;
};

type propTypes = {
  settings: IClientNotificationSettings,
  canEdit: boolean,
  isSaving: boolean,
  onSave: (settings: IClientNotificationSettings) => void,
  onCancel?: () => void,
};

export const NotificationSettingsForm = ({
  settings,
  canEdit,
  isSaving,
  onSave,
  onCancel,
}: propTypes) => {
  const theme = useTheme();

  const [draft, setDraft] = useState<IClientNotificationSettings>(settings);
  const [validationMessage, setValidationMessage] = useState('');

  // Start again from the saved settings whenever they are (re)loaded
  const [loadedSettings, setLoadedSettings] = useState(settings);
  if (settings !== loadedSettings) {
    setLoadedSettings(settings);
    setDraft(settings);
    setValidationMessage('');
  }

  const timezoneOptions = useMemo(() =>
    draft.timezone && !timezones.includes(draft.timezone) ?
      [draft.timezone, ...timezones] :
      timezones,
    [draft.timezone]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const setSummaryEmails = (emails: string[]) => {
    // Several addresses can be pasted at once, separated by commas, semicolons or spaces
    const split = emails.flatMap(e => e.split(/[\s,;]+/)).filter(e => e !== '');
    const unique = split.filter((e, i) =>
      split.findIndex(other => other.toLowerCase() === e.toLowerCase()) === i);
    setDraft({...draft, summaryEmails: unique});
  };

  const setHelpdesk = (index: number, change: Partial<IHelpdeskNotificationSettings>) => {
    setDraft({
      ...draft,
      helpdesk: draft.helpdesk.map((h, i) => i === index ? {...h, ...change} : h),
    });
  };

  const handleSave = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const trimmed = {
      ...draft,
      helpdesk: draft.helpdesk.map(h => ({...h, email: h.email.trim()})),
    };
    const problem = validateNotificationSettings(trimmed);
    setValidationMessage(problem);
    if (!problem) {
      onSave(trimmed);
    }
  };

  const handleCancel = () => {
    setDraft(settings);
    setValidationMessage('');
    onCancel?.();
  };

  return (
    <form onSubmit={handleSave} data-testid="notification-settings-form">
      <SectionHeading>Timezone</SectionHeading>
      <SectionDescription>
        Daily and weekly emails are sent, and their days counted, in this timezone. Without one, {defaultTimezone} is used.
      </SectionDescription>
      <Autocomplete
        options={timezoneOptions}
        value={draft.timezone || null}
        onChange={(_event, value) => setDraft({...draft, timezone: value ?? ''})}
        disabled={!canEdit}
        sx={{width: '400px'}}
        renderInput={(params) =>
          <TextField
            {...params}
            placeholder={`${defaultTimezone} (default)`}
            slotProps={{...params.slotProps, htmlInput: {...params.slotProps.htmlInput, 'data-testid': 'timezone-input'}}}
          />}
      />

      <DtxSpacer space={3}/>
      <SectionHeading>Daily and weekly email recipients</SectionHeading>
      <SectionDescription>
        These addresses get the daily notification and weekly summary emails. They do not need to be portal users.
        With no recipients, neither email is sent.
      </SectionDescription>
      <Autocomplete
        multiple
        freeSolo
        // Keeps an address that was typed but not confirmed with Enter
        autoSelect
        options={[]}
        value={draft.summaryEmails}
        onChange={(_event, value) => setSummaryEmails(value as string[])}
        disabled={!canEdit}
        renderValue={(value, getItemProps) =>
          value.map((email, index) => {
            const {key, ...itemProps} = getItemProps({index});
            return <Chip
              key={key}
              label={email}
              color={isValidEmail(email) ? 'default' : 'error'}
              {...itemProps}
            />;
          })
        }
        renderInput={(params) =>
          <TextField
            {...params}
            placeholder={canEdit ? "Type an address and press Enter" : ""}
            slotProps={{...params.slotProps, htmlInput: {...params.slotProps.htmlInput, 'data-testid': 'summary-emails-input'}}}
          />}
      />

      <DtxSpacer space={3}/>
      <SectionHeading>Helpdesk integration</SectionHeading>
      <SectionDescription>
        Each helpdesk address gets an email per user that needs attention, for the kinds of user selected.
      </SectionDescription>
      {draft.helpdesk.length === 0 &&
        <Typography sx={{fontStyle: 'italic'}}>No helpdesk integration.</Typography>}
      {draft.helpdesk.map((helpdesk, index) => (
        <Box
          key={index}
          sx={{display: 'flex', alignItems: 'center', gap: theme.spacing(2), marginBottom: theme.spacing(1)}}
        >
          <TextField
            value={helpdesk.email}
            placeholder="helpdesk@example.com"
            onChange={(event) => setHelpdesk(index, {email: event.target.value})}
            disabled={!canEdit}
            sx={{width: '320px'}}
            slotProps={{htmlInput: {'data-testid': `helpdesk-email-${index}`}}}
          />
          <FormGroup row>
            {helpdeskFlags.map(({key, label}) =>
              <FormControlLabel
                key={key}
                label={label}
                disabled={!canEdit}
                control={<Checkbox
                  checked={helpdesk[key]}
                  onChange={(event) => setHelpdesk(index, {[key]: event.target.checked})}
                  slotProps={{input: {'aria-label': `${label} for helpdesk ${index + 1}`}}}
                />}
              />
            )}
          </FormGroup>
          {canEdit && <IconButton
            aria-label={`Remove helpdesk ${index + 1}`}
            onClick={() => setDraft({...draft, helpdesk: draft.helpdesk.filter((_h, i) => i !== index)})}
          >
            <DeleteOutlinedIcon/>
          </IconButton>}
        </Box>
      ))}
      {canEdit && <DefaultButton
        variant="outlined"
        onClick={() => setDraft({...draft, helpdesk: [...draft.helpdesk, {...newHelpdesk}]})}
      >
        Add helpdesk
      </DefaultButton>}

      {draft.slackFeatures && <>
        <DtxSpacer space={3}/>
        <SectionHeading>Slack notifications</SectionHeading>
        <SectionDescription>
          Sent to the Slack channel configured for this client.
        </SectionDescription>
        <FormGroup row>
          {slackFlags.map(({key, label}) =>
            <FormControlLabel
              key={key}
              label={label}
              disabled={!canEdit}
              control={<Checkbox
                checked={draft.slackFeatures![key]}
                onChange={(event) => setDraft({
                  ...draft,
                  slackFeatures: {...draft.slackFeatures!, [key]: event.target.checked},
                })}
              />}
            />
          )}
        </FormGroup>
      </>}

      {validationMessage &&
        <Typography sx={{color: theme.palette.error.main, marginTop: theme.spacing(2)}} role="alert">
          {validationMessage}
        </Typography>}

      {canEdit && <Box sx={{textAlign: 'right', marginTop: theme.spacing(3)}}>
        {isSaving ?
          <CircularProgress size={31}/> :
          <>
            <DefaultButton
              sx={{marginRight: theme.spacing()}}
              variant="contained"
              onClick={handleCancel}
              disabled={!isDirty && !onCancel}
            >
              {onCancel ? 'Cancel' : 'Discard changes'}
            </DefaultButton>
            <DefaultButton
              variant="contained"
              type="submit"
              disabled={!isDirty}
            >
              Save
            </DefaultButton>
          </>}
      </Box>}
    </form>
  );
};
